/**
 * login-passwordless-email-code/index.tsx
 * Auth0 ACUL screen: "login-passwordless-email-code"
 *
 * WHAT THIS SCREEN DOES
 * ---------------------
 * Collects the 6-digit OTP code sent to the user's email address.
 * Reached when Auth0 routes to this screen from the email passwordless
 * connection (typically after a /authorize?connection=email request).
 *
 * The user can:
 *   - Enter the 6-digit code and click "Log in"
 *   - Resend the code (with a 30-second cooldown)
 *   - Switch to password login (triggers a new passwordless request or
 *     redirects back to login-password — see TODO below)
 *   - Go back to the identifier screen
 *
 * ALTERNATE METHOD
 * ----------------
 * "Log in with password instead" links back to the database connection.
 * TODO: wire up properly once your tenant has both email-passwordless
 * AND a database connection enabled.
 *
 * HOW TO EXTEND
 * -------------
 * Captcha support: call `handleSubmitEmailCode({ code, captcha })` when
 * `isCaptchaAvailable` is true — see the existing IdentifierForm in ./components/.
 */

import { AuthLayout } from "@/components/auth/AuthLayout";
import { OtpForm } from "@/components/auth/OtpForm";
import { loadIdentifier } from "@/utils/navigation";
import { applyAuth0Theme } from "@/utils/theme/themeEngine";
import { useLoginPasswordlessEmailCodeManager } from "./hooks/useLoginPasswordlessEmailCodeManager";

function LoginPasswordlessEmailCodeScreen() {
  const {
    loginPasswordlessEmailCode,
    texts,
    locales,
    data,
    handleSubmitEmailCode,
    handleResendEmailCode,
    handleSwitchToPasswordless,
  } = useLoginPasswordlessEmailCodeManager();

  document.title =
    texts?.pageTitle || locales?.page?.title || "Check your email";
  applyAuth0Theme(loginPasswordlessEmailCode);

  // The email shown in the prompt — prefer Auth0 SDK data, fallback to session
  const emailTarget =
    ((data as Record<string, unknown> | undefined)?.email as string) ??
    (loadIdentifier() || "your email address");

  const handleSubmit = (code: string) => {
    handleSubmitEmailCode({ code });
  };

  const handleResend = () => {
    handleResendEmailCode();
  };

  const handleBack = () => {
    window.history.back();
  };

  // TODO: wire this to a redirect that starts a DB-connection password flow
  const handleSwitchToPassword = () => {
    handleSwitchToPasswordless();
  };

  return (
    <AuthLayout title="Check your email">
      <OtpForm
        target={emailTarget}
        onSubmit={handleSubmit}
        onResend={handleResend}
        onBack={handleBack}
        alternateLabel="Log in with password instead"
        onAlternate={handleSwitchToPassword}
        submitLabel="Log in"
      />
    </AuthLayout>
  );
}

export default LoginPasswordlessEmailCodeScreen;
